import { initialErrors } from '../Constants/OrderStateGroups.constants';
import { OrderStateGroupActionTypes } from '../Constants/Types';
import OrderStateGroupType from '../Interfaces/order-state-groups.interface';

const initialState: OrderStateGroupType['status'] = {
  orderStateGroups: [],
  unmappedStatuses: [],
  lorderStateGroups: false,
  error: initialErrors,
};

const OrderStateGroupsReducer = (
  state: OrderStateGroupType['status'] = initialState,
  action: OrderStateGroupType['action'],
): OrderStateGroupType['status'] => {
  switch (action.type) {
    case OrderStateGroupActionTypes.FETCH_ORDER_STATE_GROUPS:
      return {
        ...state,
        orderStateGroups: [...(action.payload.orderStateGroups || [])].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0),
        ),
      };

    case OrderStateGroupActionTypes.FETCH_UNMAPPED_ORDER_STATUSES:
      return {
        ...state,
        unmappedStatuses: action.payload || [],
      };

    case OrderStateGroupActionTypes.CREATE_ORDER_STATE_GROUP: {
      // Create's response is partial (title/color only); fill the derived
      // fields with safe defaults so the column renders immediately. A
      // refetch follows to replace this entry with the canonical record.
      const nextOrder =
        state.orderStateGroups.reduce(
          (max, g) => (g.order > max ? g.order : max),
          -1,
        ) + 1;
      const newGroup: OrderStateGroupType['orderStateGroup'] = {
        statuses: [],
        order: nextOrder,
        state: 'Active',
        statusCount: 0,
        totalOrders: 0,
        ...action.payload,
      };
      return {
        ...state,
        orderStateGroups: [...state.orderStateGroups, newGroup],
      };
    }

    case OrderStateGroupActionTypes.UPDATE_ORDER_STATE_GROUP:
      return {
        ...state,
        orderStateGroups: state.orderStateGroups.map((g) =>
          g._id === action.payload._id ? { ...g, ...action.payload } : g,
        ),
      };

    case OrderStateGroupActionTypes.DELETE_ORDER_STATE_GROUP: {
      const { id } = action.payload;
      // The deleted group's statuses become unmapped. Push them into the
      // optimistic slice so the Unmapped row reflects the move immediately;
      // the post-mutation refetch replaces this with the authoritative list
      // (which also has accurate counts).
      const deleted = state.orderStateGroups.find((g) => g._id === id);
      const freed = deleted?.statuses ?? [];
      const existing = new Set(state.unmappedStatuses.map((s) => s.status));
      const additions = freed
        .filter((s) => !existing.has(s))
        .map((status) => ({ status, count: 0 }));
      return {
        ...state,
        orderStateGroups: state.orderStateGroups.filter((g) => g._id !== id),
        unmappedStatuses: [...state.unmappedStatuses, ...additions],
      };
    }

    case OrderStateGroupActionTypes.ASSIGN_STATUS_TO_GROUP: {
      const { status, groupId } = action.payload as {
        status: string;
        groupId: string | null;
      };
      // Remove the status from every group, then append to the target. This
      // keeps the mapping single-valued without needing the source groupId
      // in the payload.
      const nextGroups = state.orderStateGroups.map((g) => {
        const without = g.statuses.filter((s) => s !== status);
        if (g._id === groupId) {
          return { ...g, statuses: [...without, status] };
        }
        return without.length === g.statuses.length
          ? g
          : { ...g, statuses: without };
      });
      // Keep the unmapped slice in sync with the move:
      //   • Assign to a group → drop the status from unmapped.
      //   • Unassign (groupId: null) → add the status back (count 0 until
      //     the post-mutation refetch supplies the real count).
      let nextUnmapped = state.unmappedStatuses;
      if (groupId === null) {
        if (!state.unmappedStatuses.some((s) => s.status === status)) {
          nextUnmapped = [...state.unmappedStatuses, { status, count: 0 }];
        }
      } else if (state.unmappedStatuses.some((s) => s.status === status)) {
        nextUnmapped = state.unmappedStatuses.filter(
          (s) => s.status !== status,
        );
      }
      return {
        ...state,
        orderStateGroups: nextGroups,
        unmappedStatuses: nextUnmapped,
      };
    }

    case OrderStateGroupActionTypes.START_ORDER_STATE_GROUPS:
      return { ...state, lorderStateGroups: true };

    case OrderStateGroupActionTypes.END_ORDER_STATE_GROUPS:
      return { ...state, lorderStateGroups: false };

    case OrderStateGroupActionTypes.ERROR_ORDER_STATE_GROUP:
      return {
        ...state,
        error: action.payload
          ? { ...state.error, [action.field!]: action.payload }
          : { ...state.error, [action.field!]: null },
      };

    case OrderStateGroupActionTypes.RESET_ORDER_STATE_GROUP:
      return { ...state, error: initialErrors };

    default:
      return state;
  }
};

export default OrderStateGroupsReducer;
